/**
 * Bir düğünün etkinlikleri — ana tarih + opsiyonel ikinci etkinlik (kına,
 * nikah, after-party…).
 *
 * ⚠️ İkinci etkinlik ana tarihten ÖNCE de SONRA da olabilir (tipik örnek:
 * 16 Ağustos kına, 23 Ağustos düğün). Buradaki hiçbir fonksiyon sıra varsayımı
 * yapmaz; her şey kronolojik karşılaştırmadan türer.
 *
 * Bu modül geri sayım gösteren TÜM yüzeylerin tek kaynağıdır (guest ana sayfa,
 * join countdown, Onyx countdown, organizer/admin dashboard, app kabukları) —
 * "hangi etkinliğe sayıyoruz" sorusu tek yerde cevaplanır.
 */
import type { Event2Label } from "./types";
import { tr, en, fr, es, type LanguageCode } from "./locales";

/** Ham DB satırı (snake_case) ya da API cevabı — her iki panel/app aynı şekli görür. */
export interface WeddingEventSource {
  dateTime: string | null;
  location: string | null;
  /** Ana etkinliğin türü. `null` = eski davranış ("Düğün"). */
  event1Label: Event2Label | null;
  event1LabelCustom: string | null;
  event2DateTime: string | null;
  event2Location: string | null;
  event2Label: Event2Label | null;
  event2LabelCustom: string | null;
}

export interface ActiveWeddingEvent {
  which: "primary" | "event2";
  dateTime: string;
  location: string | null;
  /** `"primary"` = etiketsiz ana etkinlik → UI "Düğün" yazar (geriye dönük). */
  label: Event2Label | "primary";
  /** Yalnız `label === "other"` iken dolu. */
  customLabel: string | null;
  isPast: boolean;
}

/**
 * API cevapları snake_case döner (`date_time`, `event2_date_time`), TS tipleri
 * camelCase. Her tüketicide aynı dönüşümü yazmamak için tek yardımcı.
 */
export interface WeddingEventRow {
  date_time?: string | null;
  location?: string | null;
  event1_label?: string | null;
  event1_label_custom?: string | null;
  event2_date_time?: string | null;
  event2_location?: string | null;
  event2_label?: string | null;
  event2_label_custom?: string | null;
}

export function eventSourceFromRow(row: WeddingEventRow | null | undefined): WeddingEventSource {
  return {
    dateTime: row?.date_time ?? null,
    location: row?.location ?? null,
    event1Label: (row?.event1_label as Event2Label | null) ?? null,
    event1LabelCustom: row?.event1_label_custom ?? null,
    event2DateTime: row?.event2_date_time ?? null,
    event2Location: row?.event2_location ?? null,
    event2Label: (row?.event2_label as Event2Label | null) ?? null,
    event2LabelCustom: row?.event2_label_custom ?? null,
  };
}

function toMs(dateTime: string): number {
  return new Date(dateTime).getTime();
}

/** Etkinlik adayları — kronolojik sıraya göre DEĞİL, tanım sırasına göre. */
function candidates(src: WeddingEventSource, nowMs: number): ActiveWeddingEvent[] {
  const out: ActiveWeddingEvent[] = [];
  if (src.dateTime && !Number.isNaN(toMs(src.dateTime))) {
    const label = src.event1Label ?? "primary";
    out.push({
      which: "primary",
      dateTime: src.dateTime,
      location: src.location,
      label,
      customLabel: label === "other" ? src.event1LabelCustom : null,
      isPast: toMs(src.dateTime) <= nowMs,
    });
  }
  if (src.event2DateTime && !Number.isNaN(toMs(src.event2DateTime))) {
    const label = src.event2Label ?? "other";
    out.push({
      which: "event2",
      dateTime: src.event2DateTime,
      location: src.event2Location,
      label,
      customLabel: label === "other" ? src.event2LabelCustom : null,
      isPast: toMs(src.event2DateTime) <= nowMs,
    });
  }
  return out;
}

/**
 * Geri sayımın hedefleyeceği etkinlik: gelecektekilerden EN YAKIN olanı.
 * Hepsi geçmişse kronolojik olarak EN SON olanı (`isPast: true`) döner —
 * böylece "X gün önce" gösteren yüzeyler de aynı kaynaktan beslenir.
 * Hiç tarih yoksa `null`.
 */
export function resolveActiveWeddingEvent(
  src: WeddingEventSource,
  nowMs: number = Date.now(),
): ActiveWeddingEvent | null {
  const all = candidates(src, nowMs);
  if (all.length === 0) return null;

  const upcoming = all
    .filter((e) => !e.isPast)
    .sort((a, b) => toMs(a.dateTime) - toMs(b.dateTime));
  if (upcoming.length > 0) return upcoming[0];

  return all.reduce((latest, e) => (toMs(e.dateTime) > toMs(latest.dateTime) ? e : latest));
}

/**
 * Tek "başlık" tarih/mekan gösteren yüzeylerin (join hero/mekan bölümü, ana
 * sayfa mekan kartı) HANGİ etkinliği göstereceği: geri sayımın hedeflediği
 * AKTİF etkinlik. İki tarihli düğünde ilk tarih geçince başlık kendiliğinden
 * ikinci etkinliğe döner — organizatör hiçbir alan değiştirmez, tema fark
 * etmez. Sıra varsayımı yok: hangisi "birincil" (`date_time`) olursa olsun,
 * en yakın gelecekteki etkinliğe döner.
 *
 * `listWeddingEvents` (program) yine TÜM etkinlikleri taşır; bu yalnız
 * tek-değer gösteren yüzeyler için — geri sayımla (resolveActiveWeddingEvent)
 * aynı etkinliği gösterip tutarlı kalsınlar diye.
 *
 * Hiç geçerli tarih yoksa ham birincil değerlere düşer (boş düğün formu).
 */
export function headlineEvent(
  src: WeddingEventSource,
  nowMs: number = Date.now(),
): { dateTime: string | null; location: string | null } {
  const active = resolveActiveWeddingEvent(src, nowMs);
  return {
    dateTime: active?.dateTime ?? src.dateTime,
    location: active?.location ?? src.location,
  };
}

/**
 * Tüm etkinlikler kronolojik sırayla — "iki etkinliği de göster" isteyen
 * yüzeyler için (RSVP başlığı, düğün detay kartı). Geri sayım İÇİN DEĞİL.
 */
export function listWeddingEvents(
  src: WeddingEventSource,
  nowMs: number = Date.now(),
): ActiveWeddingEvent[] {
  return candidates(src, nowMs).sort((a, b) => toMs(a.dateTime) - toMs(b.dateTime));
}

/** İkinci etkinlik tanımlı mı? Tek doğruluk testi: tarih dolu mu. */
export function hasSecondEvent(src: WeddingEventSource): boolean {
  return !!src.event2DateTime;
}

/**
 * Bir etkinliğe kalan tam gün sayısı — takvim günü farkı (saat farkı DEĞİL),
 * yoksa "yarın akşam 20:00" bugün 21:00'de bakınca "0 gün" görünürdü.
 *   > 0  → o kadar gün kaldı
 *   = 0  → bugün
 *   < 0  → o kadar gün önceydi
 */
export function daysUntilEvent(dateTime: string, nowMs: number = Date.now()): number {
  const target = new Date(dateTime);
  if (Number.isNaN(target.getTime())) return 0;
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOf(target) - startOf(new Date(nowMs))) / 86_400_000);
}

const DICTS = { tr, en, fr, es };

/**
 * Etkinliğin insan-okur adı: "Kına", "Düğün", ya da `other` ise organizatörün
 * yazdığı serbest metin.
 *
 * Sözlüğe DOĞRUDAN bakar (react-i18next'e bağlı değil) çünkü worker tarafı da
 * (SMS `{{event2_name}}`) aynı adı üretmek zorunda. Anahtarların dört dilde de
 * var olduğunu TypeScript derleme anında garanti eder — `locales.test.ts`
 * küme eşitliğiyle ikinci savunma hattı.
 */
export function weddingEventName(
  event: Pick<ActiveWeddingEvent, "label" | "customLabel">,
  lang: LanguageCode = "tr",
): string {
  const d = DICTS[lang] ?? tr;
  if (event.label === "primary") return d.wedding.mainEventLabel;
  switch (event.label) {
    case "kina": return d.wedding.event2Label.kina;
    case "nikah": return d.wedding.event2Label.nikah;
    case "dugun": return d.wedding.event2Label.dugun;
    case "after_party": return d.wedding.event2Label.afterParty;
    case "other": return event.customLabel?.trim() || d.wedding.event2Label.other;
  }
}
